import { useEffect, useMemo, useState } from 'react'

// useListSwap provides everything needed to maintain a list of generic items
// where you want to provide the ability to include/exclude any subset of those items.
// RETURNS:
// toInclude - the items currently in the included list
// toExclude - the items currently in the excluded list
// onInclude - the action to include one item from the list
// onExclude - the action to exclude one item from the list
// includeAll - this action will set the toInclude list to contain all and toExclude list empty
// excludeAll - this action will set the toExclude list to contain all and toInclude list empty
export function useListSwap<T>({
  items,
  sort,
}: {
  items: T[]
  sort?: (t1: T, t2: T) => number
}) {
  const [allItems, setAllItems] = useState<T[]>([])
  const [toInclude, setToInclude] = useState<T[]>([])
  const [toExclude, setToExclude] = useState<T[]>([])

  useEffect(() => {
    setAllItems([...items].sort(sort))
  }, [items, sort])

  // CO-1910: updated the list setting logic to
  // only clear exclude list in the case that any item that is currently
  // in toExclude does not exist in allItems; also only add items
  // into toInclude if they are not present in the toExclude list
  useEffect(() => {
    setToExclude((prev) => {
      if (
        allItems.length !== 0 &&
        prev.find((item) => !allItems.includes(item))
      ) {
        return []
      }
      return prev
    })
    setToInclude([...allItems].filter((item) => !toExclude.includes(item)))
  }, [allItems, toExclude])

  const props = useMemo(() => {
    // This function could be changed to take any number of items
    // Maybe should verify the item is in the allItems set
    function onExclude(...items: T[]) {
      setToExclude((prev) => {
        const filtered = items.filter((i) => !prev.includes(i))
        return [...prev, ...filtered].sort(sort)
      })
      setToInclude((prev) => prev.filter((p) => !items.includes(p)))
    }

    // This function could be changed to take any number of items
    // Maybe should verify the item is in the allItems set
    function onInclude(...items: T[]) {
      setToInclude((prev) => {
        const filtered = items.filter((i) => !prev.includes(i))
        return [...prev, ...filtered].sort(sort)
      })
      setToExclude((prev) => prev.filter((p) => !items.includes(p)))
    }

    function includeAll() {
      setToInclude([...allItems])
      setToExclude([])
    }
    function excludeAll() {
      setToExclude([...allItems])
      setToInclude([])
    }
    return {
      onExclude,
      onInclude,
      includeAll,
      excludeAll,
    }
  }, [allItems, sort])

  return {
    ...props,
    toInclude,
    toExclude,
  }
}
